import { useEffect, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { FaFileInvoice, FaArrowLeft, FaSearch } from 'react-icons/fa'
import { FiX } from 'react-icons/fi'
import api from '../../../libs/axios'
import { useDebounce } from '../../../hooks/useDebounce'

interface EligibleSite {
  id: number
  site_name?: string
  reference_number?: string
  project?: { project_name: string }
  state_circle?: { name: string }
  site_engineer?: { first_name: string; last_name: string }
}

export default function VendorPortalEligibleBillingSites() {
  const [sites, setSites] = useState<EligibleSite[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [filing, setFiling] = useState<number | null>(null)
  const [filed, setFiled] = useState<Set<number>>(new Set())
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  // Debounced search — auto-fires 400 ms after typing stops
  const debouncedSearch = useDebounce(searchInput, 400)

  const fetchSites = useCallback(() => {
    setLoading(true)
    const params: any = { page, limit: 20 }
    if (debouncedSearch) params.search = debouncedSearch
    api.get('/vendor-portal/billings/eligible-sites', { params })
      .then(r => {
        setSites(r.data.data?.rows ?? [])
        setTotal(r.data.data?.count ?? 0)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [page, debouncedSearch])

  useEffect(() => { fetchSites() }, [fetchSites])

  // Keep manual Search button working; debounce handles auto-search
  const handleSearch = () => { setSearch(searchInput); setPage(1) }
  const clearSearch = () => { setSearchInput(''); setSearch(''); setPage(1) }

  const handleFileBilling = async (siteId: number) => {
    setFiling(siteId)
    try {
      await api.post('/vendor-portal/billings', { project_site_id: siteId })
      setFiled(prev => { const next = new Set(prev); next.add(siteId); return next })
    } catch {
      // toast handled by axios interceptor
    } finally {
      setFiling(null)
    }
  }

  return (
    <>
      <Helmet><title>Vendor Portal – File Billing</title></Helmet>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Link to="/vendor-portal/billings" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
            <FaArrowLeft className="w-3.5 h-3.5" /> Back to Billing
          </Link>
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">File Billing</h1>
          <p className="text-sm text-gray-500">{total} sites eligible for billing (report sent to client)</p>
        </div>

        {/* Search */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex items-center">
            <FaSearch className="absolute left-3 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search site name / reference..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-72"
            />
            {searchInput && (
              <button onClick={clearSearch} className="absolute right-2 text-gray-400 hover:text-gray-600">
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Search
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Site Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Reference</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Project</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Circle</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Engineer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(6)].map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>
                      ))}
                    </tr>
                  ))
                ) : sites.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-gray-400">No eligible sites for billing</td>
                  </tr>
                ) : sites.map(site => (
                  <tr key={site.id} className={`hover:bg-gray-50/50 ${filed.has(site.id) ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{site.site_name ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{site.reference_number ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{site.project?.project_name ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{site.state_circle?.name ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {site.site_engineer ? `${site.site_engineer.first_name} ${site.site_engineer.last_name}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {filed.has(site.id) ? (
                        <span className="text-xs text-green-600 font-medium">✓ Billing Filed</span>
                      ) : (
                        <button
                          onClick={() => handleFileBilling(site.id)}
                          disabled={filing === site.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          <FaFileInvoice className="w-3.5 h-3.5" />
                          {filing === site.id ? 'Filing...' : 'File Billing'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > 20 && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
              <span>Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} of {total}</span>
              <div className="flex gap-2">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border border-gray-200 rounded disabled:opacity-40">Prev</button>
                <button disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border border-gray-200 rounded disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
